/**
 * Rasterize an in-DOM <svg> to a PNG and trigger a download.
 *
 * Serializes the live SVG node, loads it into an Image via a blob URL (same
 * origin, so the canvas is never tainted), draws it onto a canvas scaled up for
 * crispness, then downloads the PNG. The SVG must carry explicit width/height
 * attributes (the share card does) so the Image rasterizes at the right size.
 */
export function exportSvgElementToPng(
  svg: SVGSVGElement,
  filename: string,
  scale = 2,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const vb = svg.viewBox?.baseVal;
    const w = (vb && vb.width) || svg.width.baseVal.value || 1080;
    const h = (vb && vb.height) || svg.height.baseVal.value || 1080;

    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(svgUrl);
          reject(new Error('Canvas 2D context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(svgUrl);
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('PNG encode failed'));
            return;
          }
          const dlUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = dlUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(dlUrl);
          resolve();
        }, 'image/png');
      } catch (err) {
        URL.revokeObjectURL(svgUrl);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      reject(new Error('SVG image failed to load'));
    };
    img.src = svgUrl;
  });
}

/** kebab-case a build name into a safe filename stem. */
export function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'arrow'
  );
}
